import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { FileText, CreditCard, MessageSquare, ArrowRight, LogOut, Loader2 } from 'lucide-react';
import { api } from '@/lib/api';
import { SiteFooter } from '@/components/SiteFooter';

const SCORE_COLORS = {
  'Strong': 'bg-emerald-50 text-emerald-700 border-emerald-200',
  'Viable': 'bg-blue-50 text-blue-700 border-blue-200',
  'Fragile': 'bg-amber-50 text-amber-700 border-amber-200',
  'High Risk': 'bg-orange-50 text-orange-700 border-orange-200',
  'Not Investable': 'bg-red-50 text-red-700 border-red-200',
};

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, login, logout, loading: authLoading } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { login(); return; }
    loadDashboard();
  }, [user, authLoading]);

  const loadDashboard = async () => {
    try {
      const d = await api.getDashboard();
      setData(d);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  if (authLoading || loading) return (
    <div className="min-h-screen bg-[#FBFBFD] flex items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-[#86868B]" />
    </div>
  );

  if (!user) return null;

  const formatDate = (d) => {
    if (!d) return '';
    return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const isAdmin = user?.email === 'rohit.chidurala@gmail.com';
  const freeUsed = data?.user?.free_reports_used || 0;
  const userPlan = data?.user?.plan || 'free';
  const freeExhausted = !isAdmin && userPlan === 'free' && freeUsed >= 1;

  return (
    <div className="min-h-screen bg-[#FBFBFD]">
      <nav className="sticky top-0 z-50 backdrop-blur-xl bg-white/70 border-b border-[#E5E5EA]">
        <div className="max-w-6xl mx-auto px-6 md:px-12 flex items-center justify-between h-16">
          <button onClick={() => navigate('/')}><img src="https://customer-assets.emergentagent.com/job_unit-economics-3/artifacts/z3ko7m68_Cake_Studio.png" alt="ROCH" className="h-14 w-auto" /></button>
          <div className="flex items-center gap-4">
            {freeExhausted ? (
              <Button data-testid="upgrade-btn" onClick={() => navigate('/plans')} className="bg-[#0071E3] text-white hover:bg-[#0077ED] rounded-full px-4 md:px-6 py-2 text-xs md:text-sm">
                Upgrade to Continue
              </Button>
            ) : (
              <Button data-testid="new-assessment-btn" onClick={() => navigate('/assess')} className="bg-[#1D1D1F] text-white hover:bg-[#434344] rounded-full px-4 md:px-6 py-2 text-xs md:text-sm">
                New Assessment
              </Button>
            )}
            <Button data-testid="logout-btn" onClick={logout} variant="ghost" className="text-[#86868B]" size="icon">
              <LogOut className="h-4 w-4" strokeWidth={1.5} />
            </Button>
          </div>
        </div>
      </nav>

      <div className="max-w-6xl mx-auto px-6 md:px-12 py-12 md:py-16">
        {/* Header */}
        <div className="mb-12">
          <p className="text-xs tracking-wide uppercase font-semibold text-[#86868B] mb-2">Dashboard</p>
          <h1 className="text-3xl md:text-4xl font-semibold text-[#1D1D1F] tracking-tight">
            Welcome, {user?.name?.split(' ')[0] || 'there'}.
          </h1>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3 md:gap-6 mb-12">
          <div className="bg-white rounded-3xl border border-[#E5E5EA] p-6 md:p-8">
            <FileText className="h-5 w-5 text-[#86868B] mb-3" strokeWidth={1.5} />
            <div className="text-3xl font-semibold text-[#1D1D1F]">{data?.reports?.length || 0}</div>
            <div className="text-xs text-[#86868B] mt-1">Reports</div>
          </div>
          <div className="bg-white rounded-3xl border border-[#E5E5EA] p-6 md:p-8">
            <CreditCard className="h-5 w-5 text-[#86868B] mb-3" strokeWidth={1.5} />
            <div className="text-3xl font-semibold text-[#1D1D1F]">{data?.user?.credits || 0}</div>
            <div className="text-xs text-[#86868B] mt-1">Credits</div>
          </div>
          <div className="bg-white rounded-3xl border border-[#E5E5EA] p-6 md:p-8">
            <MessageSquare className="h-5 w-5 text-[#86868B] mb-3" strokeWidth={1.5} />
            <div className="text-3xl font-semibold text-[#1D1D1F]">{data?.consultations?.length || 0}</div>
            <div className="text-xs text-[#86868B] mt-1">Consultations</div>
          </div>
        </div>

        {/* Free Trial Exhausted Banner */}
        {freeExhausted && (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 mb-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4" data-testid="trial-exhausted-banner">
            <div>
              <p className="text-sm font-bold text-amber-800">Free trial report used</p>
              <p className="text-xs text-amber-700/70 font-medium mt-0.5">Choose a plan to get full financial analysis, AI insights, and scenario testing.</p>
            </div>
            <Button
              onClick={() => navigate('/plans')}
              className="bg-amber-600 text-white hover:bg-amber-700 rounded-full px-5 py-2 text-xs font-semibold flex-shrink-0"
            >
              View Plans
            </Button>
          </div>
        )}

        {/* Tabs */}
        <Tabs defaultValue="reports" data-testid="dashboard-tabs">
          <TabsList className="bg-[#F5F5F7] rounded-full p-1 mb-8">
            <TabsTrigger data-testid="tab-reports" value="reports" className="rounded-full text-sm data-[state=active]:bg-white">Reports</TabsTrigger>
            <TabsTrigger data-testid="tab-payments" value="payments" className="rounded-full text-sm data-[state=active]:bg-white">Payments</TabsTrigger>
            <TabsTrigger data-testid="tab-consultations" value="consultations" className="rounded-full text-sm data-[state=active]:bg-white">Consultations</TabsTrigger>
          </TabsList>

          <TabsContent value="reports">
            {(!data?.reports || data.reports.length === 0) ? (
              <div className="text-center py-16">
                <p className="text-[#86868B] mb-4">No reports yet.</p>
                <Button data-testid="start-first-assessment" onClick={() => navigate('/assess')} className="bg-[#1D1D1F] text-white hover:bg-[#434344] rounded-full px-6">
                  Start Your First Assessment <ArrowRight className="ml-2 h-4 w-4" strokeWidth={1.5} />
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {data.reports.map((r) => (
                  <button
                    key={r.report_id}
                    data-testid={`report-card-${r.report_id}`}
                    onClick={() => navigate(`/report/${r.report_id}`)}
                    className="w-full bg-white rounded-3xl border border-[#E5E5EA] p-6 flex items-center justify-between text-left transition-all duration-500 hover:-translate-y-0.5 hover:shadow-[0_8px_32px_rgba(0,0,0,0.04)]"
                  >
                    <div>
                      <div className="flex items-center gap-3 mb-1">
                        <span className="text-[#1D1D1F] font-medium">{r.model_label}</span>
                        <Badge className={`text-xs rounded-full border ${SCORE_COLORS[r.score?.label]}`}>{r.score?.label}</Badge>
                        <Badge variant="outline" className="text-xs rounded-full">{r.plan}</Badge>
                      </div>
                      <p className="text-xs text-[#86868B]">Score: {r.score?.total}/100 &middot; {formatDate(r.created_at)}</p>
                    </div>
                    <ArrowRight className="h-4 w-4 text-[#86868B]" strokeWidth={1.5} />
                  </button>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="payments">
            {(!data?.payments || data.payments.length === 0) ? (
              <div className="text-center py-16">
                <p className="text-[#86868B]">No payments yet.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {data.payments.map((p) => (
                  <div key={p.payment_id} className="bg-white rounded-3xl border border-[#E5E5EA] p-6 flex items-center justify-between">
                    <div>
                      <span className="text-[#1D1D1F] font-medium capitalize">{p.plan?.replace('_', ' ')}</span>
                      <p className="text-xs text-[#86868B] mt-1">{formatDate(p.created_at)}</p>
                    </div>
                    <div className="text-right">
                      <div className="text-[#1D1D1F] font-medium">₹{(p.amount / 100).toLocaleString()}</div>
                      <Badge variant="outline" className="text-xs rounded-full mt-1">{p.status}</Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="consultations">
            {(!data?.consultations || data.consultations.length === 0) ? (
              <div className="text-center py-16">
                <p className="text-[#86868B]">No consultations booked yet.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {data.consultations.map((c) => (
                  <div key={c.consultation_id} className="bg-white rounded-3xl border border-[#E5E5EA] p-6">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[#1D1D1F] font-medium capitalize">{c.consultation_type?.replace('_', ' ')}</span>
                      <Badge variant="outline" className="text-xs rounded-full capitalize">{c.status}</Badge>
                    </div>
                    {c.preferred_date && <p className="text-xs text-[#86868B]">Preferred date: {c.preferred_date}</p>}
                    {c.notes && <p className="text-xs text-[#86868B] mt-1">Notes: {c.notes}</p>}
                    <p className="text-xs text-[#86868B] mt-1">Requested: {formatDate(c.created_at)}</p>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
      <SiteFooter />
    </div>
  );
}
